import React, { useState, useEffect } from "react";
import { I } from "../data";
import { useAuth } from "../AuthContext";
import { useWorkspace } from "../WorkspaceContext";
import { formatApiError } from "../api";
import { useConfirm } from "../components/ConfirmDialog";
import { useUpgrade, FEATURES } from "../UpgradeContext";
import LockedCard from "../components/LockedCard";
import UpgradeButton from "../components/UpgradeButton";
import { COUNTRIES } from "../data/countries";
import { degreesForStage } from "../Onboarding";

const ACADEMIC_STAGES = [
  "", "High School - Junior", "High School - Senior",
  "Undergraduate - 1st Year", "Undergraduate - 2nd Year", "Undergraduate - 3rd Year", "Undergraduate - 4th Year",
  "Graduate - 1st Year", "Graduate - 2nd Year",
];

// Field configuration — grouped by section
const SECTIONS = [
  {
    key: "academic",
    label: "Academic",
    eyebrow: "01 — Academic foundation",
    fields: [
      { key: "academic_stage", label: "Academic stage", type: "select", options: ACADEMIC_STAGES },
      { key: "board", label: "Board / curriculum", type: "select",
        options: ["", "CBSE", "ICSE", "IB Diploma", "A-Levels", "SLC + 12", "High School Diploma (US)", "Other"] },
      { key: "gpa", label: "GPA / %", type: "text", placeholder: "e.g. 3.8 / 85%" },
      { key: "predicted_grades", label: "Predicted grades", type: "text", placeholder: "e.g. AAA / 42 points" },
      { key: "last_academic_subject_taken", label: "Last academic subject taken", type: "text", placeholder: "e.g. AP Calculus, A-Level Physics" },
      { key: "strongest_subjects", label: "Strongest subjects", type: "tags", placeholder: "e.g. Math, CS, Physics" },
    ],
  },
  {
    key: "target",
    label: "Target & direction",
    eyebrow: "02 — Where you're headed",
    fields: [
      { key: "intended_degree_level", label: "Degree level", type: "select",
        options: ["", "Bachelors", "Masters", "PhD"] },
      { key: "intended_major", label: "Intended major", type: "text", placeholder: "e.g. Computer Science" },
      { key: "backup_fields", label: "Backup fields", type: "tags", placeholder: "e.g. Data Science, Econ" },
      { key: "target_countries", label: "Target countries", type: "tags", placeholder: "e.g. United States, Canada" },
      { key: "intake_term", label: "Target intake", type: "select",
        options: ["", "Fall 2026", "Spring 2027", "Fall 2027", "Spring 2028"] },
    ],
  },
  {
    key: "finance",
    label: "Financial",
    eyebrow: "03 — How you'll fund it",
    fields: [
      { key: "budget", label: "Budget", type: "select",
        options: ["", "Need full scholarship", "Up to $20,000 / year", "$20,000 – $40,000 / year", "$40,000 – $70,000 / year", "Budget is not a constraint"] },
      { key: "funding_need", label: "Funding need", type: "select",
        options: ["", "self", "partial", "full", "loan"] },
    ],
  },
  {
    key: "tests",
    label: "Test scores",
    eyebrow: "04 — Language & standardized tests",
    fields: [
      { key: "english_test", label: "English test", type: "select",
        options: ["", "IELTS", "TOEFL", "Duolingo", "PTE", "Waived", "Not yet taken"] },
      { key: "english_score", label: "English score", type: "text", placeholder: "e.g. 7.5 / 110" },
      { key: "english_test_date", label: "English test date", type: "text", placeholder: "e.g. Mar 2026" },
      { key: "sat_score", label: "SAT", type: "text", placeholder: "e.g. 1480" },
      { key: "act_score", label: "ACT", type: "text", placeholder: "e.g. 32" },
      { key: "gre_score", label: "GRE", type: "text", placeholder: "e.g. 325" },
      { key: "gmat_score", label: "GMAT", type: "text", placeholder: "e.g. 710" },
    ],
  },
  {
    key: "profile_signals",
    label: "Your story",
    eyebrow: "05 — What makes you, you",
    fields: [
      { key: "research_experience", label: "Research experience", type: "textarea", placeholder: "Labs, papers, projects…" },
      { key: "internships", label: "Internships", type: "textarea", placeholder: "Companies, roles, duration" },
      { key: "extracurriculars", label: "Extracurriculars", type: "textarea", placeholder: "Clubs, volunteering, arts…" },
      { key: "leadership", label: "Leadership", type: "textarea", placeholder: "Roles, teams led, outcomes" },
    ],
  },
  {
    key: "preferences",
    label: "Preferences & constraints",
    eyebrow: "06 — What Vidhya should keep in mind",
    fields: [
      { key: "preferences", label: "Preferences", type: "textarea", placeholder: "e.g. Research-heavy programs, cities, climate…" },
      { key: "constraints", label: "Constraints", type: "textarea", placeholder: "e.g. Diet, family, single-gender, visa history…" },
    ],
  },
  {
    key: "geo",
    label: "Geography",
    eyebrow: "07 — Where you're coming from",
    fields: [
      { key: "student_country", label: "Home country", type: "country", placeholder: "e.g. Nepal" },
      { key: "country_of_citizenship", label: "Country of citizenship", type: "country", placeholder: "e.g. Nepal" },
    ],
  },
];

function toArray(v) {
  if (Array.isArray(v)) return v;
  if (!v) return [];
  return String(v).split(",").map((x) => x.trim()).filter(Boolean);
}

export default function Profile() {
  const { user } = useAuth();
  const { profile, completeness, gmail, updateProfile, refresh, connectGmail, disconnectGmail } = useWorkspace();
  const confirm = useConfirm();
  const isNavigator = !!user?.plan_summary?.is_navigator;
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [gmailMsg, setGmailMsg] = useState("");

  useEffect(() => {
    const p = profile || {};
    const next = { name: user?.name || "", ...p };
    // Display-only backfill for users created before the schema rebuild — never
    // saved unless the user explicitly hits Save.
    if (!next.intended_degree_level && p.intended_level) {
      next.intended_degree_level = { undergraduate: "Bachelors", masters: "Masters", phd: "PhD" }[String(p.intended_level).toLowerCase()] || "";
    }
    if (!next.academic_stage && /high school|undergrad|graduate/i.test(p.grade_level || "")) {
      next.academic_stage = p.grade_level;
    }
    setDraft(next);
  }, [profile, user]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const onConnectGmail = async () => {
    try {
      const data = await connectGmail();
      const w = window.open(data.url, "_blank", "width=520,height=640");
      const onMsg = (ev) => {
        if (ev.data && ev.data.type === "gmail-connected") {
          window.removeEventListener("message", onMsg);
          setTimeout(() => window.location.reload(), 800);
        }
      };
      window.addEventListener("message", onMsg);
      // Fallback poll
      const poll = setInterval(async () => {
        if (w && w.closed) { clearInterval(poll); window.location.reload(); }
      }, 1500);
    } catch (e) {
      setGmailMsg(formatApiError(e));
    }
  };

  const onDisconnectGmail = async () => {
    const ok = await confirm({
      title: "Disconnect Gmail?",
      message: "Vidhya will stop drafting and sending emails through your Google account. You can reconnect any time.",
      confirmLabel: "Disconnect",
      danger: true,
    });
    if (!ok) return;
    try {
      await disconnectGmail();
      setGmailMsg("Gmail disconnected.");
    } catch (e) {
      setGmailMsg(formatApiError(e));
    }
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    // Client-side academic guardrail (mirrors backend) for instant feedback.
    if (draft.academic_stage && draft.intended_degree_level &&
        !degreesForStage(draft.academic_stage).includes(draft.intended_degree_level)) {
      setErr(`A ${draft.academic_stage} student can't apply directly to a ${draft.intended_degree_level} program. Valid next step(s): ${degreesForStage(draft.academic_stage).join(", ")}.`);
      setSaving(false);
      return;
    }
    try {
      // Normalize tag fields to arrays
      const patch = { ...draft };
      ["strongest_subjects", "backup_fields", "target_countries"].forEach((k) => {
        if (patch[k] !== undefined) patch[k] = toArray(patch[k]);
      });
      await updateProfile(patch);
      await refresh();
      setSavedAt(Date.now());
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const pct = completeness?.percent ?? 0;
  const ringDash = 251;
  const ringOffset = ringDash - (ringDash * pct) / 100;
  const coreItems = [
    { key: "academic", label: "Academic info", ok: !!(draft.gpa || draft.predicted_grades) },
    { key: "english", label: "English test", ok: !!(draft.english_test && draft.english_test !== "Not yet taken") },
    { key: "major", label: "Intended major", ok: !!draft.intended_major },
    { key: "country", label: "Home country", ok: !!draft.student_country },
    { key: "sat_score", label: "SAT score", ok: !!draft.sat_score },
    { key: "extracurriculars", label: "Extracurriculars", ok: !!draft.extracurriculars },
  ];

  return (
    <div className="vwrap vprofile" data-testid="profile-view">
      <div className="ptitle">Your profile</div>
      <div className="psub">The more Vidhya knows, the sharper your advice, fit scores, and autofill get.</div>

      {err && <div className="auth-error" data-testid="profile-error" style={{ marginBottom: 14 }}>{err}</div>}

      <div className="pcols">
        {/* LEFT — profile strength */}
        <div>
          <div className="pcard2" data-testid="profile-completeness">
            <div className="pmeter">
              <div className="pring">
                <svg width="96" height="96" viewBox="0 0 96 96">
                  <circle cx="48" cy="48" r="40" fill="none" stroke="var(--indigo-50)" strokeWidth="9" />
                  <circle cx="48" cy="48" r="40" fill="none" stroke="var(--lemon)" strokeWidth="9" strokeLinecap="round" strokeDasharray={ringDash} strokeDashoffset={ringOffset} />
                </svg>
                <span className="pp">{pct}%</span>
              </div>
              <h4>Profile strength</h4>
              <p>{completeness?.core_filled || 0}/{completeness?.core_total || 10} core slots filled. {pct < 100 ? "Add the missing pieces to sharpen every match." : "Your profile is in great shape."}</p>
            </div>
            <div className="pcompl">
              {coreItems.map((c) => (
                <div key={c.key} className={`pc ${c.ok ? "ok" : "todo"}`} data-testid={`profile-core-${c.key}`}>
                  {c.ok ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M20 6 9 17l-5-5" /></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /></svg>
                  )}
                  {c.label}
                </div>
              ))}
            </div>
            <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.55, marginTop: 14, marginBottom: 0 }} data-testid="profile-fill-hint">
              Fill in your details on the right and hit <strong>Save profile</strong> — Vidhya uses them to sharpen your advice and matches.
            </p>
          </div>
        </div>

        {/* RIGHT — editable detail */}
        <div className="profile-grid" style={{ display: "block" }}>
          <div className="profile-identity" data-testid="profile-identity" style={{ marginBottom: 16 }}>
            <div className="avatar-lg">{(draft.name || user?.email || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label className="p-label">Full name</label>
              <input className="input" value={draft.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Your full name" data-testid="profile-name-input" />
              <div className="p-email">{user?.email}</div>
            </div>
          </div>

          {SECTIONS.map((sec) => {
            const navOnly = !isNavigator && ["tests", "profile_signals", "preferences"].includes(sec.key);
            if (navOnly) {
              return (
                <LockedCard
                  key={sec.key}
                  feature={FEATURES.FULL_PROFILE}
                  context={`profile_section_${sec.key}`}
                  title={sec.label}
                  description="Vidhya uses these signals to sharpen every advisor reply, email draft, and match. Unlock with Navigator."
                  ctaLabel="Unlock with Navigator"
                  testid={`profile-locked-${sec.key}`}
                >
                  <div style={{ padding: 6 }}><div className="eyebrow">{sec.eyebrow}</div></div>
                </LockedCard>
              );
            }
            return (
              <div key={sec.key} className="profile-section" data-testid={`profile-section-${sec.key}`} style={{ marginBottom: 16 }}>
                <div className="p-section-head">
                  <div className="eyebrow">{sec.eyebrow}</div>
                  <h3 className="p-section-title">{sec.label}</h3>
                </div>
                <div className="p-fields">
                  {sec.fields.map((f) => (
                    <FieldInput key={f.key} field={f} value={draft[f.key]} onChange={(v) => set(f.key, v)} />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Your data, the honest version */}
          <div className="pcard2" data-testid="profile-data-honesty" style={{ marginBottom: 16 }}>
            <h3 className="p-section-title" style={{ marginBottom: 14 }}>Your data, the honest version</h3>
            <div className="stored">
              <div className="storecol yes">
                <h5><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M20 6 9 17l-5-5" /></svg>Vidhya stores</h5>
                <ul>
                  <li>• Academic info &amp; test scores</li>
                  <li>• Your documents &amp; essay drafts</li>
                  <li>• Reusable autofill answers</li>
                  <li>• Email drafts (synced to Gmail)</li>
                </ul>
              </div>
              <div className="storecol no">
                <h5><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M18 6 6 18M6 6l12 12" /></svg>Never stored</h5>
                <ul>
                  <li>• Passport &amp; national ID numbers</li>
                  <li>• Bank / proof-of-funds amounts</li>
                  <li>• University portal passwords</li>
                  <li>→ You enter these in the portal yourself</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Gmail / connected accounts */}
          <div className="profile-section gmail-card" data-testid="profile-gmail-card" style={{ marginBottom: 16 }}>
            <div className="p-section-head">
              <div className="eyebrow">Integration · Gmail</div>
              <h3 className="p-section-title">Connect your <em>Gmail</em></h3>
            </div>
            {!isNavigator ? (
              <div style={{ paddingTop: 6 }}>
                <p className="muted" style={{ fontSize: 13, lineHeight: 1.55, margin: "0 0 12px" }}>
                  Gmail outreach — drafting, sending, and tracking professor replies — unlocks with Navigator.
                </p>
                <UpgradeButton feature={FEATURES.GMAIL} context="profile_gmail_card" label="Unlock Gmail with Navigator" testid="profile-gmail-upgrade-btn" />
              </div>
            ) : (
              <div className="gmail-status">
                {gmail?.connected ? (
                  <>
                    <div className="gmail-row">
                      <div className="gmail-dot connected" />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500 }}>Connected</div>
                        <div className="muted" style={{ fontSize: 12 }}>{gmail.email}</div>
                      </div>
                      <button className="btn btn-sm btn-ghost" onClick={onDisconnectGmail} data-testid="gmail-disconnect-btn">Disconnect</button>
                    </div>
                    <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.6, marginTop: 12, marginBottom: 0 }}>
                      Vidhya can now save AI-drafted outreach as real Gmail drafts, send from your own inbox, and pull professor replies into your tracker.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="gmail-row">
                      <div className="gmail-dot" />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500 }}>Not connected</div>
                        <div className="muted" style={{ fontSize: 12 }}>One-click consent. We only request Gmail send + read scopes.</div>
                      </div>
                      <button className="btn btn-clay" onClick={onConnectGmail} data-testid="gmail-connect-btn">Connect Gmail</button>
                    </div>
                    <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.6, marginTop: 12, marginBottom: 0 }}>
                      Once connected, AI-drafted professor outreach can be saved straight to your Gmail drafts.
                    </p>
                  </>
                )}
                {gmailMsg && <div className="auth-error" style={{ marginTop: 10 }}>{gmailMsg}</div>}
              </div>
            )}
          </div>

          <div className="profile-save-bar">
            <div style={{ flex: 1, color: "var(--slate)", fontSize: 13 }}>
              {savedAt ? (
                <span className="p-saved"><I.Check size={12} /> Saved · Vidhya will reference this in your next chat.</span>
              ) : (
                <span>Changes are used by the AI advisor for all future chats, emails, and matches.</span>
              )}
            </div>
            <button className="btn btn-clay" onClick={save} disabled={saving} data-testid="profile-save-btn">
              {saving ? "Saving…" : "Save profile"}
            </button>
          </div>

          <div className="card" data-testid="profile-support-card" style={{ padding: "16px 18px", marginTop: 14, fontSize: 13, lineHeight: 1.55 }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--slate)", marginBottom: 6 }}>Need help?</div>
            <div style={{ color: "var(--ink-2)" }}>
              Email us at <a href="mailto:hello@govidhya.com" data-testid="support-email-link" style={{ color: "var(--indigo)" }}>hello@govidhya.com</a>{" "}
              or DM <a href="https://instagram.com/vidhyahq" target="_blank" rel="noopener noreferrer" data-testid="support-instagram-link" style={{ color: "var(--indigo)" }}>@vidhyahq</a> on Instagram.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const render = () => {
    if (field.type === "select") {
      return (
        <select className="input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`profile-${field.key}`}>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>{opt || `Select ${field.label.toLowerCase()}…`}</option>
          ))}
        </select>
      );
    }
    if (field.type === "country") {
      return (
        <>
          <input
            className="input"
            list="profile-countries"
            value={value || ""}
            placeholder={field.placeholder}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`profile-${field.key}`}
          />
          <datalist id="profile-countries">
            {COUNTRIES.map((c) => <option key={c} value={c} />)}
          </datalist>
        </>
      );
    }
    if (field.type === "textarea") {
      return (
        <textarea
          className="input"
          rows={3}
          value={value || ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`profile-${field.key}`}
        />
      );
    }
    if (field.type === "tags") {
      const display = Array.isArray(value) ? value.join(", ") : (value || "");
      return (
        <input
          className="input"
          value={display}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`profile-${field.key}`}
        />
      );
    }
    return (
      <input
        className="input"
        value={value || ""}
        placeholder={field.placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`profile-${field.key}`}
      />
    );
  };
  return (
    <div className="p-field">
      <label className="p-label">{field.label}</label>
      {render()}
    </div>
  );
}

function humanizeKey(k) {
  return k.replace(/_/g, " ");
}
